import React, { useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import FroalaEditor from 'froala-editor';
import { css } from '@emotion/core';

import froalaConfig from '../../config/froalaConfig.js';
import { basicToolbarStyle, froalaPaddingStyle, textboxStyle } from '../../style/froalaCss.js';
import extendLang, { getLangCode } from '../../lang/extendLang.js';

import { handleOverLengthString } from '../../utils/util.js';
import { createImgProp } from '../../utils/image.js';

import _debounce from 'lodash/debounce';

// Js
import 'froala-editor/js/languages/zh_tw.js';
import 'froala-editor/js/froala_editor.pkgd.min.js';
import 'froala-editor/js/plugins.pkgd.min.js';

// Custom
import '../../custom/customButton.js';
import '../../custom/customSelect.js';

// Css
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/plugins/code_view.min.css';
import '../../style/froalaCustom.css';
import { paragraphClass } from '../../config/config.js';

const BasicFroalaEditor = forwardRef(
    (
        {
            froalaKey,
            content,
            onUpdateContent,
            editorLang,
            clientLang,
            translate = () => {},
            onUploadImageResponse = () => {},
            onImageFailed = () => {},
            stringMaxLength,
            uploadOptions = {},
            configs = {},
            elementId = 'froala-editor',
            isShowPadding = false,
            type,
            className,
            fixDistance = 0,
            isCloseButton = false,
            customStyle,
            customButtonStyle
        },
        ref
    ) => {
        const containerRef = useRef(null);
        const topRef = useRef(0);
        const editorLangCode = getLangCode(editorLang);
        const clientLangCode = Boolean(clientLang) ? getLangCode(clientLang) : editorLangCode;

        const fixToolbar = () => {
            if (containerRef.current) {
                const containerTop = containerRef.current.getBoundingClientRect().top;
                const containerHeight = containerRef.current.offsetHeight;
                const toolbar = containerRef.current.querySelector('.fr-toolbar');
                if (Boolean(toolbar)) {
                    if (containerTop + containerHeight < fixDistance) {
                        // scroll bottom hide
                        toolbar.style.top = `-${fixDistance}px`;
                    } else if (containerTop < fixDistance) {
                        // scroll inside elemet fix top
                        toolbar.style.top = `${fixDistance}px`;
                    } else {
                        // follow scrollbar top
                        toolbar.style.top = `${containerTop}px`;
                    }

                    topRef.current = containerTop;
                }
            }
        };

        const extractIdFromContent = content => {
            const idMatch = content.match(/id="([^"]+)"/);
            return idMatch ? idMatch[1] : `id${Date.now()}`;
        };

        const handleBlockClick = ({ type }) => {
            const eleId = content ? extractIdFromContent(content) : `id${Date.now()}`;
            const editingArea = document.getElementById(elementId);
            let defaultContent = '';
            let editorOptions = {
                toolbarInline: true,
                toolbarVisibleWithoutSelection: true,
                events: {
                    focus: function () {
                        if (typeof this.toolbar !== 'undefined') {
                            window.addEventListener('scroll', fixToolbar, { passive: true });
                        }
                    },
                    blur: function () {
                        window.removeEventListener('scroll', fixToolbar, { passive: true });
                        if (stringMaxLength) handleOverLengthString(this, stringMaxLength);
                        //TODO: toolbarHide 不行 ， this toolbar hide 是 undefined ask froala
                        // if (this.toolbar?.hide) {
                        //     console.log('this', this);
                        //     this.toolbar?.hide();
                        // }
                    },
                    'commands.after': function (cmd, param1) {
                        adjustPopupPosition(this);
                        switch (cmd) {
                            case 'paragraphFormat':
                                if (param1 === 'DIV') {
                                    const selectionBlocks = this.selection.blocks();
                                    // 如果選擇了 div，並且當前選取區塊不是 div，應該進行轉換

                                    this.selection.save();
                                    Array.from(selectionBlocks).forEach(elem => {
                                        elem.style.removeProperty('font-size');
                                        const innerElements = elem.querySelectorAll('span');
                                        if (innerElements.length > 0) {
                                            Array.from(innerElements).forEach(innerElem => {
                                                innerElem.style.removeProperty('font-size');
                                            });
                                        }
                                        this.html.wrap('<div></div>');
                                        // 重新取得已包裹的元素
                                        const wrappedBlock = this.selection.blocks()[0];
                                        // 設定 class 名稱或樣式
                                        wrappedBlock.className = paragraphClass[param1] || '';
                                    });
                                    this.selection.restore();
                                } else if (
                                    !this.format.is(param1, { class: paragraphClass[param1] })
                                ) {
                                    const selectionBlocks = this.selection.blocks();
                                    Array.from(selectionBlocks).forEach(elem => {
                                        elem.style.removeProperty('font-size');

                                        const innerElements = elem.querySelectorAll('span');
                                        if (innerElements.length > 0) {
                                            Array.from(innerElements).forEach(innerElem => {
                                                innerElem.style.removeProperty('font-size');
                                            });
                                        }
                                    });
                                    this.paragraphStyle.apply(paragraphClass[param1]);
                                }
                                break;
                            default:
                                break;
                        }
                    },
                    'popups.show.image.edit': function () {
                        // 取得 `toolbar 然後隱藏掉`
                        const toolbar = document.querySelector(
                            '.fr-toolbar.fr-ltr.fr-desktop.fr-inline'
                        );
                        if (toolbar) {
                            toolbar.style.display = 'none';
                        }
                    }
                }
            };

            if (type === 'image') {
                defaultContent = `<img id="${eleId}" src="https://assets.fevercdn.com/widget/icon-01.svg" />`;
                editorOptions = {
                    ...editorOptions,
                    imageEditButtons: ['imageReplace', 'imageAlt', 'imageSize'],
                    charCounterCount: false, // 隱藏字數統計
                    placeholderText: '', // 移除預設的占位文字
                    // enter: FroalaEditor.ENTER_P, // 確保默認邏輯被覆蓋
                    htmlAllowedTags: ['img'], // 僅允許圖片標籤
                    htmlAllowedEmptyTags: [],
                    imageInsertButtons: ['imageUpload'], // 只允許上傳圖片
                    multiLine: true,
                    // 攔截與文字相關的事件
                    events: {
                        ...editorOptions.events,
                        // 禁止鍵盤輸入
                        keydown: function (e) {
                            e.preventDefault();
                            return false;
                        },
                        keypress: function (e) {
                            e.preventDefault();
                            return false;
                        },
                        keyup: function (e) {
                            e.preventDefault();
                            return false;
                        },
                        // 禁止點擊和粘貼文字
                        'paste.before': function () {
                            return false;
                        },
                        click: function (e) {
                            //為了解決 點擊圖片後 按下enter 會出現空白的行數
                            const target = e.target;

                            // 確保點擊的是圖片
                            if (target.tagName === 'IMG') {
                                setTimeout(() => {
                                    adjustPopupPosition(this);
                                }, 0);
                            }
                            const parentElement = e.target.closest('.fr-element.fr-view');
                            if (parentElement) {
                                // 設置 contenteditable 為 false
                                parentElement.setAttribute('contenteditable', 'false');
                            }
                        },
                        'image.inserted': function (imgElement) {
                            createImgProp(imgElement);
                            adjustPopupPosition(this);
                        },
                        'image.replaced': function (imgElement) {
                            createImgProp(imgElement);
                            adjustPopupPosition(this);
                        },
                        'image.loaded': function (imgElement) {
                            createImgProp(imgElement);
                            setTimeout(() => {
                                const popup = document.querySelector('.fr-popup.fr-active');
                                const image = document.querySelector('.fr-image-resizer.fr-active');
                                if (popup) {
                                    popup.classList.remove('fr-active');
                                    image.classList.remove('fr-active');
                                }
                            }, 400);
                        },
                        'image.resizeEnd': function (imgElement) {
                            createImgProp(imgElement);
                        },
                        'image.error': function (error) {
                            if (error.code === 5) onImageFailed('IMG', 'img.upload_failed');
                            else if (error.code === 6) onImageFailed('IMG', 'img.support_exts');
                            else if (error.code === 8)
                                onImageFailed('IMG', 'img.upload_failed_size');
                            else onImageFailed('IMG', 'listfeed.upload_failed');
                        },
                        'image.uploaded': function (response) {
                            let image = '';
                            if (onUploadImageResponse) image = onUploadImageResponse(response);
                            else {
                                const {
                                    items: { url }
                                } = JSON.parse(response);
                                image = url;
                            }
                            this.image.insert(image, false, null, this.image.get(), response);
                            return false;
                        }
                    }
                };
            } else if (type === 'button') {
                defaultContent = `<a class="custom-button-plugin" 
                style="display: inline-block; margin: 8px; padding: 14px 40px; 
                       letter-spacing: 1px; cursor: pointer; text-decoration: none !important; 
                    color: ${Boolean(customButtonStyle) ? customButtonStyle : 'rgb(255, 255, 255)'};
                       border-radius: 5px; border-color: transparent;" 
                id="${eleId}">
               <p>${isCloseButton ? '關閉視窗' : '邀請好友'}</p>
             </a>`;

                editorOptions = {
                    ...editorOptions,
                    linkEditButtons: [
                        'buttonStyle',
                        '|',
                        'textColorEdit',
                        'backgroundColorEdit',
                        'borderOuterButton',
                        'buttonAlign',
                        'buttonPadding'
                    ]
                };
            } else if (type === 'textarea') {
                defaultContent = `<div id="${eleId}"><h4 >揪好友刮好運</h4><div >有機會抽中五星飯店住宿券</div></div>`;
                editorOptions = {
                    ...editorOptions,
                    toolbarButtons: {
                        moreText: {
                            buttons: [
                                'paragraphFormat',
                                'fontFamily',
                                'fontSize',
                                '|',
                                'bold',
                                'textColor',
                                'backgroundColor',
                                'align',
                                'customSelect',
                                'italic',
                                'strikeThrough',
                                'subscript',
                                'superscript',
                                'clearFormatting',
                                'formatOL',
                                'formatUL',
                                'lineHeight',
                                'indent',
                                'outdent',
                                'customQuote'
                            ],
                            align: 'left',
                            buttonsVisible: 8
                        },
                        align: 'right',
                        buttonsVisible: 2
                    }
                };
            }

            // 新增區塊到編輯區域
            const block = document.createElement('div');
            block.className = 'block';
            block.setAttribute(
                'style',
                'width: 100%; word-break: break-word; text-overflow: ellipsis;'
            );
            if (isCloseButton) {
                block.id = 'closeButton';
            }
            block.innerHTML = content || defaultContent;
            editingArea.appendChild(block);

            // 初始化 Froala Editor
            const editorInstance = new FroalaEditor(`#${eleId}`, {
                key: froalaKey,
                language: editorLangCode,
                ...froalaConfig({ translate, uploadOptions, clientLangCode }),
                ...editorOptions,
                ...configs,
                events: {
                    ...editorOptions.events,
                    ...(Boolean(configs.events) ? configs.events : {}),
                    contentChanged: _debounce(function () {
                        const content = this.$el[0].outerHTML;
                        if (isCloseButton && type === 'button') {
                            type = 'closeButton';
                        }
                        onUpdateContent(type, content);
                    }, 300) // 300ms debounce
                }
            });
            editorInstance.baseFroalaEditorRef = ref;
        };

        const adjustPopupPosition = editorInstance => {
            const element = editorInstance.$el[0]; // 取得編輯器主元素
            // console.log('editorInstance', editorInstance.$el);
            // console.log('editorInstance', editorInstance.$el[0].getBoundingClientRect());

            // console.log('element', element);

            const popup = document.querySelector('.fr-popup.fr-active'); // 取得 button 的 popup
            // const img = document.querySelector('div.fr-image-resizer.fr-active'); // 取得當前選中的圖片

            if (popup && element) {
                const { x: elementLeft, y: elementTop } =
                    editorInstance.$el[0].getBoundingClientRect();
                const popupHeight = popup.offsetHeight;
                const popupWidth = popup.offsetWidth;

                // 使用 element.x 和 element.y
                // const imgTop = element.y;
                // const imgLeft = element.x;
                const elementHeight = element.offsetHeight;
                const elementWidth = element.offsetWidth;

                const windowHeight = window.innerHeight;
                const windowWidth = window.innerWidth;

                const distanceToBottom = windowHeight - (elementTop + elementHeight);

                // console.log('imgTop', imgTop);
                // console.log('imgLeft', imgLeft);
                // console.log('windowHeight', windowHeight);
                // console.log('windowWidth', windowWidth);
                // console.log('popupHeight', popupHeight);
                // console.log('popupWidth', popupWidth);
                // 調整 `top` 位置
                if (distanceToBottom < popupHeight) {
                    // 如果圖片下面空間不足，將 popup 移到圖片上方
                    popup.style.top = `${elementTop - popupHeight}px`;
                }

                // 調整 `left` 位置，確保 popup 不會超出螢幕邊界
                let popupLeft = elementLeft;

                // 如果 `popup` 會超出右側螢幕範圍
                if (popupLeft + popupWidth > windowWidth) {
                    popupLeft = elementLeft + elementWidth - popupWidth; // 向左對齊
                }

                // 如果 `popup` 會超出左側螢幕範圍
                if (popupLeft < 0) {
                    popupLeft = 0; // 貼齊螢幕左邊
                }
            }
        };

        useImperativeHandle(ref, () => ({
            handleBlockClick
        }));

        useEffect(() => {
            handleBlockClick({ type });
        }, []);

        useEffect(() => {
            extendLang({ langCode: editorLangCode, translate });
        }, [translate, editorLangCode]);

        return (
            <div
                css={css(textboxStyle, basicToolbarStyle(), froalaPaddingStyle(isShowPadding))}
                style={customStyle}
                {...(Boolean(elementId) ? { id: elementId } : {})}
                className={`${Boolean(className) ? className : ''} froala-container`}
                ref={containerRef}
            ></div>
        );
    }
);

export default BasicFroalaEditor;
